/**
 * Route guard — admits only coaches (user.is_coach).
 *
 * Mirrors ProtectedRoute, with an extra coach check:
 *   - hydrating auth: spinner
 *   - not authenticated: redirect to /login (preserving attempted URL)
 *   - authenticated non-coach: redirect to / (home)
 *
 * Backend enforces the same boundary (403) on coach-only endpoints;
 * this is the matching UX so non-coaches never see the dashboard chrome.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../hooks/useAuth";

export default function CoachRoute({
  children,
}: {
  children: React.ReactElement;
}) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate
        to="/login"
        // redirectedFromProtected is read by PageEventObserver to mark the
        // resulting /login page_view as a dead end (M-03). `from` is left
        // untouched: LoginPage still reads it to bounce the user back.
        state={{ from: location.pathname, redirectedFromProtected: true }}
        replace
      />;
  }

  if (!user?.is_coach) {
    return <Navigate to="/" replace />;
  }

  return children;
}
